import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { PERMISSIONS, type LeadBreakdownRow, type LeadsSummaryResponse } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { LeadInsights } from '@/components/LeadInsights';
import { LeadNetworkPanel } from '@/components/LeadNetworkPanel';
import { LeadPipeline } from '@/components/LeadPipeline';
import { LeadsTable } from '@/components/LeadsTable';
import { LeadTiming } from '@/components/LeadTiming';
import { Paginator } from '@/components/Paginator';
import { RangeBar } from '@/components/RangeBar';
import { Select } from '@/components/Select';
import { StatTile } from '@/components/StatTile';
import { TrendChart } from '@/components/TrendChart';
import { formatDuration, formatMoney, formatPercent } from '@/components/format';
import { useLeadNetwork, useLeadView, type LeadView } from '@/hooks/useLeads';
import { useLeads } from '@/hooks/useLeads';
import { MutationError } from '@/components/ops/Problem';

const SOURCE_OPTIONS = [
  { value: 'form', label: 'Landing form' },
  { value: 'chat', label: 'Widget chat' },
];

/**
 * Leads: who raised a hand, where they came from, how fast we answered, and what it was worth.
 *
 * <b>Why this tab is not part of the overview.</b> Every other tab reads <c>daily_metrics</c>,
 * where a lead is a COUNT on a date. A pipeline needs the individual rows — a stage that moves, a
 * deal value, a conversation duration — so this one reads the lead table instead. The date range
 * is shared with the other tabs (same `?days` / `?from` / `?to`), so switching here keeps the
 * period you were looking at.
 *
 * The two lead sources are deliberately shown TOGETHER with a source dimension rather than as two
 * tabs: the question that matters is which channel produces leads worth having, and that is a
 * comparison, not two separate reports.
 */
export function LeadsPage({ siteKey }: { siteKey: string }) {
  const leads = useLeads(siteKey);
  const navigate = useNavigate();
  const [view, setView] = useLeadView();
  const [exportState, setExportState] = useState<'idle' | 'working' | 'error'>('idle');

  const summary = leads.summary.data;
  const canManage = hasPermission(PERMISSIONS.leadsManage);
  const canExport = hasPermission(PERMISSIONS.analyticsExport);

  // The graph is the one lead read that costs real CPU server-side (an O(V·E) betweenness pass),
  // so it is fetched only while its view is on screen.
  const network = useLeadNetwork(siteKey, leads.filter, view === 'network');

  // Intelligence describes stored analyses of whole days, not the filtered rows — showing the
  // range bar and filters above it would imply they change what it says.
  const filtersApply = view !== 'intelligence';

  return (
    <>
      <nav className="subnav" aria-label="Leads views">
        {LEAD_VIEW_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`subnav-item${view === tab.key ? ' active' : ''}`}
            onClick={() => setView(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {filtersApply && <RangeBar
        days={leads.days}
        setDays={leads.setDays}
        from={leads.from}
        to={leads.to}
        setRange={leads.setRange}
        isCustomRange={leads.isCustomRange}
        actions={
          <>
            {/* Sync is behind the same permission as a manual ingestion run — it is one. */}
            {hasPermission(PERMISSIONS.ingestionRun) && (
              <button
                className="btn btn-ghost"
                onClick={() => leads.sync.mutate()}
                disabled={leads.sync.isPending}
              >
                {leads.sync.isPending ? 'Syncing…' : 'Sync now'}
              </button>
            )}

            {canExport && (
              <button
                className="btn btn-ghost"
                disabled={exportState === 'working'}
                onClick={async () => {
                  setExportState('working');
                  try {
                    // The SAME filter the page is showing — an export that quietly ships a
                    // different set is the bug nobody catches until a meeting disagrees with it.
                    await api.leads.export(siteKey, leads.filter);
                    setExportState('idle');
                  } catch {
                    setExportState('error');
                  }
                }}
              >
                {exportState === 'working' ? 'Preparing…' : exportState === 'error' ? 'Export failed' : 'Export CSV'}
              </button>
            )}
          </>
        }
      />}

      <SyncNotice results={leads.sync.data} />
      {leads.sync.error && <MutationError error={leads.sync.error} />}

      {view === 'intelligence' && <LeadInsights siteKey={siteKey} />}

      {filtersApply && <div className="filterbar">
        <Select
          label="Source"
          inline
          value={leads.source}
          onChange={(value) => leads.setFilter('source', value)}
          options={SOURCE_OPTIONS}
          placeholder="Both sources"
        />

        <Select
          label="Stage"
          inline
          value={leads.status}
          onChange={(value) => leads.setFilter('status', value)}
          options={(summary?.statuses ?? []).map((s) => ({
            value: s,
            label: s.charAt(0).toUpperCase() + s.slice(1),
            hint: summary?.by_status[s] ? String(summary.by_status[s]) : undefined,
          }))}
          placeholder="Every stage"
        />

        <Select
          label="Country"
          inline
          value={leads.country}
          onChange={(value) => leads.setFilter('country', value)}
          options={(summary?.countries ?? []).map((c) => ({ value: c, label: c }))}
          placeholder="Everywhere"
        />

        <label className="field field-inline">
          <span className="field-label t-label">Search</span>
          <DebouncedSearch value={leads.search} onCommit={(v) => leads.setFilter('q', v)} />
        </label>

        {leads.hasFilters && (
          <button className="btn btn-ghost" onClick={leads.clearFilters}>Clear filters</button>
        )}
      </div>}

      {view === 'network' && (
        <LeadNetworkPanel
          data={network.data}
          isLoading={network.isLoading}
          error={network.error as Error | null}
          currency={summary?.currency ?? null}
          onOpenLead={(id) => navigate(`/leads/${id}`)}
        />
      )}

      {view === 'pipeline' && <>

      {leads.summary.isLoading && <div className="empty-state"><span className="spinner" /></div>}

      {leads.summary.isError && (
        <Callout tone="error">
          Could not load leads. {(leads.summary.error as Error)?.message}
        </Callout>
      )}

      {summary && summary.total === 0 && (
        <div className="empty-state t-body">
          No leads in this range yet.
          <div className="t-label subtle" style={{ marginTop: 4 }}>
            Leads arrive from the landing form and the chat widget. Use “Sync now” to pull the
            latest, or widen the date range.
          </div>
        </div>
      )}

      {summary && summary.total > 0 && (
        <>
          {summary.truncated && (
            <Callout tone="warning">
              This range has more leads than one summary can hold — the figures below describe the
              most recent {summary.total.toLocaleString('en-US')}. Narrow the range for exact totals.
            </Callout>
          )}

          <div className="kpi-row">
            <StatTile
              label="Leads"
              value={summary.total}
              sub={sourceSplit(summary)}
            />
            <StatTile
              label="Won"
              value={summary.won_count}
              sub={`${formatPercent(summary.win_rate)} of decided`}
            />
            <StatTile
              label="Won value"
              value={formatMoney(summary.won_value, summary.currency)}
              sub={summary.average_won_value !== null
                ? `avg ${formatMoney(summary.average_won_value, summary.currency)}`
                : 'no deals closed yet'}
            />
            <StatTile
              label="Open pipeline"
              value={formatMoney(summary.pipeline_value, summary.currency)}
              sub="value still in play"
            />
            <StatTile
              label="Median first reply"
              value={formatDuration(summary.median_first_response_seconds)}
              sub="chat leads"
              tone={slowReply(summary.median_first_response_seconds) ? 'warning' : 'default'}
            />
            <StatTile
              label="Missed chats"
              value={summary.missed_count}
              sub="nobody answered"
              tone={summary.missed_count > 0 ? 'critical' : 'default'}
            />
          </div>

          <div className="lead-columns">
            <LeadPipeline summary={summary} />
            <LeadTiming summary={summary} />
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>Leads over time</h2>
              <span className="t-label subtle">captured vs won</span>
            </div>
            <div className="card-body">
              <TrendChart
                series={[
                  {
                    key: 'leads',
                    label: 'Leads',
                    color: 'var(--primary)',
                    points: summary.series.map((p) => ({ date: p.date, value: p.leads })),
                  },
                  {
                    key: 'won',
                    label: 'Won',
                    color: 'var(--success)',
                    points: summary.series.map((p) => ({ date: p.date, value: p.won })),
                  },
                ]}
              />
            </div>
          </div>

          <div className="lead-columns">
            <BreakdownTable
              title="Countries"
              caption="leads, and what they were worth"
              rows={summary.by_country}
              currency={summary.currency}
            />
            <BreakdownTable
              title="Campaigns"
              caption="first-touch attribution"
              rows={summary.by_campaign}
              currency={summary.currency}
            />
          </div>

          <div className="lead-columns">
            <Panel title="Source" rows={summary.by_channel} />
            <Panel title="Language" rows={summary.by_language} />
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>All leads</h2>
              <span className="t-label subtle">
                {canManage ? 'click Edit to set stage, value and owner' : 'read-only'}
              </span>
            </div>

            {leads.patch.error && <div className="card-body" style={{ paddingBottom: 0 }}><MutationError error={leads.patch.error} /></div>}
            {leads.list.isError ? (
              <div className="empty-state t-body note-warning">The leads could not be read — try again in a moment.</div>
            ) : (
              <LeadsTable
                rows={leads.list.data?.items ?? []}
                currency={summary.currency}
                canManage={canManage}
                saving={leads.patch.isPending}
                onSave={(id, body) => leads.patch.mutateAsync({ id, body })}
                onOpen={(id) => navigate(`/leads/${id}`)}
              />
            )}

            <Paginator
              page={leads.page}
              perPage={leads.perPage}
              total={leads.list.data?.total ?? 0}
              onPageChange={leads.setPage}
              onPerPageChange={leads.setPerPage}
              busy={leads.list.isFetching}
            />
          </div>
        </>
      )}

      </>}
    </>
  );
}

/** The section's three views. Labels say what each ANSWERS, not what it is built from. */
const LEAD_VIEW_TABS: { key: LeadView; label: string }[] = [
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'network', label: 'Network' },
  { key: 'intelligence', label: 'Intelligence' },
];

/** "412 form · 88 chat" — the split is the first thing to know about a lead total. */
function sourceSplit(summary: LeadsSummaryResponse): string {
  const parts: string[] = [];
  if (summary.by_source.form) parts.push(`${summary.by_source.form.toLocaleString('en-US')} form`);
  if (summary.by_source.chat) parts.push(`${summary.by_source.chat.toLocaleString('en-US')} chat`);
  return parts.join(' · ') || 'no leads';
}

/** Five minutes is the widely-cited cliff where inbound-lead contact rates collapse. */
function slowReply(seconds: number | null): boolean {
  return seconds !== null && seconds > 300;
}

/**
 * A ranked dimension as a TABLE, not a chart.
 *
 * Leads and money live on different scales, and the one chart form that shows both — a dual axis —
 * is the form the visualisation rules rule out. A table shows them honestly side by side.
 */
function BreakdownTable({
  title, caption, rows, currency,
}: { title: string; caption: string; rows: LeadBreakdownRow[]; currency: string | null }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <span className="t-label subtle">{caption}</span>
      </div>

      {rows.length === 0 ? (
        <div className="empty-state t-body">Nothing recorded yet.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{title.replace(/s$/, '')}</th>
                <th className="num">Leads</th>
                <th className="num">Won</th>
                <th className="num">Value</th>
                <th className="num">First reply</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 12).map((row) => (
                <tr key={row.value}>
                  <td>{row.value}</td>
                  <td className="num">{row.leads.toLocaleString('en-US')}</td>
                  <td className="num">{row.won > 0 ? row.won.toLocaleString('en-US') : <span className="subtle">—</span>}</td>
                  <td className="num">{row.won_value > 0 ? formatMoney(row.won_value, currency) : <span className="subtle">—</span>}</td>
                  <td className="num">{formatDuration(row.avg_first_response_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: LeadBreakdownRow[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        <span className="t-label subtle">{rows.length}</span>
      </div>
      <div className="card-body">
        <BarList
          rows={rows.map((r) => ({ label: r.value, value: r.leads }))}
          limit={8}
          emptyLabel="Not recorded yet."
        />
      </div>
    </div>
  );
}

/**
 * What the last sync did, per source.
 *
 * Reported rather than swallowed: a chat source that is failing while the form source works looks
 * exactly like "no chat leads this week" if nobody says otherwise.
 */
function SyncNotice({ results }: { results: { source: string; fetched: number; written: number; skip_reason: string | null; error: string | null }[] | undefined }) {
  if (!results || results.length === 0) return null;

  const failed = results.filter((r) => r.error);
  const skipped = results.filter((r) => r.skip_reason);
  const ok = results.filter((r) => !r.error && !r.skip_reason);

  return (
    <>
      {failed.length > 0 && (
        <Callout tone="error">
          {failed.map((r) => `${r.source}: ${r.error}`).join(' · ')}
        </Callout>
      )}
      {ok.length > 0 && (
        <Callout tone="info">
          Synced {ok.map((r) => `${r.fetched.toLocaleString('en-US')} ${r.source}`).join(' · ')}.
          {skipped.length > 0 && ` Not configured: ${skipped.map((r) => r.source).join(', ')}.`}
        </Callout>
      )}
    </>
  );
}

/**
 * The search box, debounced: the URL (and so the two lead queries) change 300 ms after the last keystroke, not on every
 * one. The URL stays the source of truth — "Clear filters" or the back button reset the box.
 */
function DebouncedSearch({ value, onCommit }: { value: string; onCommit: (value: string) => void }) {
  const [draft, setDraft] = useState(value);
  const commit = useRef(onCommit);
  commit.current = onCommit;

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return undefined;
    const timer = window.setTimeout(() => commit.current(draft), 300);
    return () => window.clearTimeout(timer);
  }, [draft, value]);

  return (
    <input
      className="input"
      value={draft}
      placeholder="name, e-mail, campaign"
      onChange={(e) => setDraft(e.target.value)}
    />
  );
}
